export default {
  // Page titles
  pageTitle: 'Event Settings | WedBox',
  settingsTitle: 'Event Settings',
  settingsDescription: 'Manage your event details',
  
  // Existing basic fields
  title: "My Events",
  myEvents: "My Events",
  description: "Create and manage your events",
  createEvent: "Create Event",
  createNew: "Create New",
  currentEvent: "Current Event",
  noEvents: "No events",
  addFirstEvent: "Create your first event",
  createFirstEvent: "Create your first event",
  eventCreated: "Event created successfully",
  eventUpdated: "Event updated",
  eventDeleted: "Event deleted",
  selectEventType: "Select event type",
  date: "Date",
  location: "Location",
  guestCount: "Guest count",
  budget: "Budget",
  status: "Status",
  active: "Active",
  draft: "Draft",
  completed: "Completed",
  cancelled: "Cancelled",
  editEvent: "Edit Event",
  deleteEvent: "Delete Event",
  confirmDelete: "Are you sure you want to delete this event?",
  yes: "Yes",
  no: "No",
  save: "Save",
  cancel: "Cancel",
  close: "Close",
  switch: "Switch",
  switchEvent: "Switch Event",
  switchedTo: "Switched to",
  currentlyActive: "Currently active",
  searchEvents: "Search events",
  noEventsFound: "No events found",
  manage: "Manage",
  yourEvents: "Your events",
  advancedView: "Advanced View",
  basicView: "Basic View",

  // Bulk actions
  bulkActions: {
    title: "Bulk Actions",
    selectAll: "Select All",
    selected: "selected",
    delete: "Delete",
    archive: "Archive",
    export: "Export",
    duplicate: "Duplicate",
    processing: "Processing...",
    confirmDelete: "Are you sure you want to delete the selected events?",
    description: "Manage multiple events at once"
  },

  // Subscription info
  subscriptionInfo: {
    free: {
      title: "Free Plan",
      description: "Great to get started"
    },
    essential: {
      title: "Essential Plan",
      description: "For small events"
    },
    pro: {
      title: "Pro Plan",
      description: "For large events"
    }
  },

  dashboard: {
    subtitle: "Overview"
  },

  maxEventsReached: "Maximum number of events reached",

  // Event types
  eventTypes: {
    wedding: "Wedding",
    engagement: "Engagement",
    birthday: "Birthday",
    babyShower: "Baby Shower",
    baptism: "Baptism",
    reunion: "Reunion",
    corporate: "Corporate Event",
    other: "Other"
  },

  // Form sections
  form: {
    eventDetails: "Event Details",
    basicInfo: "Basic Information",
    eventName: "Event Name",
    eventType: "Event Type",
    eventDate: "Event Date",
    eventLocation: "Event Location",
    partners: "Partners / Main People",
    partner1: "Partner 1 / Main Person", 
    partner2: "Partner 2 (optional)",
    firstName: "First Name",
    lastName: "Last Name",
    placeholders: {
      eventName: 'Ex: Mary and Peter\'s Wedding',
      eventLocation: 'Ex: Versailles Palace, Paris',
      firstName: 'First Name',
      lastName: 'Last Name',
    },
  },

  // Actions
  actions: {
    create: "Create",
    edit: "Edit",
    delete: "Delete",
    duplicate: "Duplicate",
    share: "Share",
    setDefault: "Set as Default",
    selectEvent: "Select Event",
  },

  // Date formatting
  dateFormat: {
    notSpecified: 'Not specified',
    invalid: 'Invalid date',
  },

  // Card details
  details: {
    title: 'Event Details',
    id: 'ID:',
    eventCount: '{{count}} events total',
    singleEvent: '1 event',
  },

  // Empty state
  empty: {
    noEventSelected: 'No event selected',
    selectEventDescription: 'Please select an event to access its settings.',
  },

  // Success messages
  success: {
    title: 'Success',
    eventUpdated: 'Event updated successfully',
  },

  // Error messages
  errors: {
    title: 'Error',
    updateFailed: 'Failed to update event',
    updateError: 'Error updating event',
    deleteFailed: 'Failed to delete event',
    deleteError: 'Error deleting event',
  },

  // Delete confirmation
  deleteConfirmation: {
    title: 'Delete Event',
    description: 'Are you sure you want to delete the event "{{eventName}}"? This action is irreversible and all associated data will be deleted.',
    confirm: 'Delete',
    cancel: 'Cancel',
  },

  // Existing features
  features: {
    aiAssistant: 'AI Assistant',
    aiAssistantDesc: 'Get personalized suggestions and intelligent help.',
    taskManagement: 'Task Management',
    taskManagementDesc: 'Organize and track all your event tasks.',
    budgetPlanning: 'Budget Planning',
    budgetPlanningDesc: 'Manage your budget with detailed tracking tools.',
    guestManagement: 'Guest Management',
    guestManagementDesc: 'Organize your guest list and their responses.',
    vendorCoordination: 'Vendor Coordination',
    vendorCoordinationDesc: 'Find and manage your vendors easily.',
    timelineTracking: 'Timeline Tracking',
    timelineTrackingDesc: 'Visualize and plan your event timeline.',
    collaborativeTools: 'Collaborative Tools',
    collaborativeToolsDesc: 'Work together with your team and loved ones.',
    documentSharing: 'Document Sharing',
    documentSharingDesc: 'Share and organize all your important documents.',
    mobileSynchronization: 'Mobile Synchronization',
    mobileSynchronizationDesc: 'Access your data anywhere with cloud sync.',
    customTemplates: 'Custom Templates',
    customTemplatesDesc: 'Create and use templates adapted to your needs.',
    realTimeNotifications: 'Real-time Notifications',
    realTimeNotificationsDesc: 'Stay informed of important updates.',
    advancedReporting: 'Advanced Reporting',
    advancedReportingDesc: 'Analyze your data with detailed reports.',
    duplicateDetection: 'Duplicate Detection',
    duplicateDetectionDesc: 'Identifies similar events based on name, type and date.'
  },

  // Test page translations
  testPage: {
    title: "🧪 Firebase Functions Test Page",
    firebaseTest: {
      title: "Firebase Function Test",
      description: "Test connectivity with Firebase functions before attempting account deletion.",
      button: "Test Firebase Function",
      result: "Result",
      userNotConnected: "❌ User not connected",
      testing: "🧪 Testing...",
      success: "✅ Test successful",
      error: "❌ Error",
      unknownError: "Unknown error",
    },
    instructions: {
      title: "📋 Instructions",
      step1: "Click \"Test Firebase Function\" above",
      step2: "If the test succeeds, go to Settings → Account",
      step3: "Try deleting your account",
      step4: "The backend function should work without asking for a password",
    },
    messages: {
      eventCreated: "✅ Event created successfully! ID: {{eventId}}",
      tasksGenerated: "🎯 {{count}} tasks generated automatically!",
      noTasksFound: "⚠️ No tasks found. Use the debug button to generate manually.",
      errorLoadingTasks: "❌ Error loading tasks",
      errorCreatingEvent: "❌ Error",
      waitingForTasks: "🔄 Waiting 3 seconds before loading tasks...",
      loadingTasks: "🔍 Loading tasks for event:",
      tasksReloaded: "🔄 Reloaded: {{count}} tasks found",
      errorReloadingTasks: "❌ Error reloading tasks",
      debuggingGeneration: "🔧 Debugging task generation...",
      manualGenerationComplete: "✅ Manual generation complete",
      debugError: "❌ Debug error",
      testingExistingEvent: "🧪 Testing with existing event...",
      eventNotFound: "❌ Event not found",
      eventFound: "📋 Event found",
      eventDate: "📅 Date",
      eventCountry: "🌍 Country",
      notSpecified: "Not specified",
      existingTasks: "📊 Existing tasks",
      generatingTasks: "🔧 Generating tasks (if necessary)...",
      generationComplete: "✅ Generation complete",
      newTasks: "📊 New tasks",
      testError: "❌ Test error",
      generatingForAllEvents: "🔄 Generating tasks for all existing events...",
      noEventsFound: "📋 No events found",
      eventsFound: "📊 {{count}} event(s) found",
      processingEvent: "🔧 Processing",
      generatingTasksFor: "🎯 Generating tasks for",
      tasksGeneratedFor: "✅ {{count}} tasks generated for",
      eventAlreadyHasTasks: "✅ {{name}} already has {{count}} tasks",
      errorProcessingEvent: "❌ Error for",
      processingComplete: "🎉 Processing complete: {{processed}} events processed, {{generated}} new tasks generated",
      globalError: "❌ Global error",
    },
    eventTypes: {
      wedding: "Wedding",
      birthday: "Birthday",
      engagement: "Engagement",
      babyShower: "Baby Shower",
      baptism: "Baptism",
      other: "Other",
    },
  },
};
